const PDF_MARGIN = 12;
const PDF_PAGE_WIDTH = 210;
const PDF_PAGE_HEIGHT = 297;
const PDF_CONTENT_WIDTH = PDF_PAGE_WIDTH - (PDF_MARGIN * 2);
const PDF_FOOTER_LIMIT = PDF_PAGE_HEIGHT - 14;

const toText = (value, fallback = '-') => {
  const normalized = String(value ?? '')
    .replace(/\u00a0/g, ' ')
    .replace(/[–—]/g, '-')
    .replace(/\s+/g, ' ')
    .trim();
  return normalized || fallback;
};

const toList = (value) => (Array.isArray(value) ? value : []);

const formatMoney = (value) => new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
}).format(Number(value || 0));

const formatDate = (value, fallback = '-') => {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeZone: 'America/Sao_Paulo',
  }).format(date);
};

const getClienteName = (cliente = {}) => {
  const name = toText(cliente.name || cliente.nome, 'Cliente');
  const digits = name.replace(/\D/g, '');
  return digits.length >= 8 ? 'Cliente' : name;
};

const getClientePhone = (cliente = {}) => toText(cliente.phone || cliente.telefone);

const getTotalOrders = (cliente = {}) => {
  const explicit = Number(cliente.totalOrders);
  return Number.isFinite(explicit) ? explicit : toList(cliente.pedidos).length;
};

const getLastActionLabel = (cliente = {}) => {
  const action = cliente.lastAction || {};
  const label = action.label || action.type || 'Sem interação';
  const date = action.date ? formatDate(action.date, '') : '';
  return [toText(label, 'Sem interação'), date].filter(Boolean).join(' - ');
};

const slugifyFilePart = (value, fallback) => {
  const slug = String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || fallback;
};

export function buildCrmDashboardPdfReport({
  clientes = [],
  summary = {},
  periodLabel = 'Todo período',
  activeFilters = ['Todos'],
  search = '',
  insightFilter = null,
} = {}) {
  const list = toList(clientes);
  const filters = toList(activeFilters).length ? activeFilters.join(', ') : 'Todos';
  const insightLabel = insightFilter === 'reativacao'
    ? 'Janela de reativação'
    : insightFilter === 'ativo'
      ? 'Ativos nesta semana'
      : 'Nenhum';

  return {
    title: 'Relatório do CRM',
    subtitle: 'Painel de clientes',
    fileName: 'crm-clientes.pdf',
    metadata: [
      ['Período', periodLabel],
      ['Filtros', filters],
      ['Busca', search || 'Nenhuma'],
      ['Insight aplicado', insightLabel],
    ],
    highlights: [
      ['Clientes no relatório', list.length],
      ['Clientes ativos', Number(summary.clientes_ativos || 0)],
      ['Precisam de atenção', Number(summary.clientes_inativos || 0)],
      ['Valor total em pedidos', formatMoney(summary.valor_total_pedidos)],
    ],
    sections: [
      {
        title: `Clientes (${list.length})`,
        columns: [
          { key: 'name', label: 'Cliente', width: 40 },
          { key: 'phone', label: 'Telefone', width: 30 },
          { key: 'status', label: 'Status', width: 22 },
          { key: 'orders', label: 'Pedidos', width: 18 },
          { key: 'spent', label: 'Total gasto', width: 29 },
          { key: 'lastAction', label: 'Última interação', width: 47 },
        ],
        rows: list.map((cliente) => ({
          name: getClienteName(cliente),
          phone: getClientePhone(cliente),
          status: toText(cliente.status),
          orders: getTotalOrders(cliente),
          spent: formatMoney(cliente.totalSpent),
          lastAction: getLastActionLabel(cliente),
        })),
        emptyLabel: 'Nenhum cliente corresponde aos filtros aplicados.',
      },
    ],
  };
}

export function buildCrmClientePdfReport({ cliente = {}, periodLabel = 'Todo período' } = {}) {
  const pedidos = toList(cliente.pedidos);
  const visitas = toList(cliente.visitas);
  const feedbacks = toList(cliente.feedbacks);
  const reservas = toList(cliente.reservas);
  const dados = cliente.dados || {};
  const name = getClienteName(cliente);
  const paymentMethods = toList(dados.paymentMethods).join(', ');
  const origins = toList(cliente.origins).join(', ');
  const intelligence = toList(dados.intelligence);

  return {
    title: 'Relatório de cliente',
    subtitle: name,
    fileName: `crm-${slugifyFilePart(name, 'cliente')}.pdf`,
    metadata: [
      ['Período', periodLabel],
      ['Telefone', getClientePhone(cliente)],
      ['Status', toText(cliente.status)],
      ['Tags', toList(cliente.tags).join(', ') || '-'],
    ],
    highlights: [
      ['Total gasto', formatMoney(cliente.totalSpent)],
      ['Total de pedidos', getTotalOrders(cliente)],
      ['Ticket médio', formatMoney(cliente.ticket || dados.averageTicket)],
      ['Última interação', getLastActionLabel(cliente)],
    ],
    keyValues: [
      ['Origem principal', dados.origin || cliente.origin],
      ['Produtos vinculados', origins],
      ['Endereço', dados.addresses],
      ['Tipo de pedido mais comum', dados.commonOrder],
      ['Métodos de pagamento', paymentMethods],
      ['Prato favorito', dados.favorite],
    ],
    sections: [
      {
        title: `Pedidos (${pedidos.length})`,
        columns: [
          { key: 'date', label: 'Data', width: 30 },
          { key: 'channel', label: 'Canal', width: 38 },
          { key: 'status', label: 'Status', width: 50 },
          { key: 'value', label: 'Valor', width: 32 },
          { key: 'items', label: 'Itens', width: 36 },
        ],
        rows: pedidos.map((pedido) => ({
          date: formatDate(pedido.date),
          channel: pedido.type,
          status: pedido.statusLabel || pedido.status || pedido.rawStatus,
          value: formatMoney(pedido.value),
          items: Number(pedido.itemCount ?? pedido.items ?? 0) || '-',
        })),
        emptyLabel: 'Sem pedidos registrados.',
      },
      {
        title: `Visitas (${visitas.length})`,
        columns: [
          { key: 'date', label: 'Data', width: 45 },
          { key: 'benefit', label: 'Benefício', width: 91 },
          { key: 'level', label: 'Nível', width: 50 },
        ],
        rows: visitas.map((visita) => ({
          date: formatDate(visita.date),
          benefit: visita.benefit ? 'Brinde retirado' : 'Sem brinde',
          level: visita.level || '-',
        })),
        emptyLabel: 'Sem visitas registradas.',
      },
      {
        title: `Feedbacks (${feedbacks.length})`,
        columns: [
          { key: 'date', label: 'Data', width: 28 },
          { key: 'score', label: 'Nota', width: 18 },
          { key: 'sentiment', label: 'Classificação', width: 38 },
          { key: 'message', label: 'Comentário', width: 102 },
        ],
        rows: feedbacks.map((feedback) => ({
          date: formatDate(feedback.date),
          score: feedback.score ?? '-',
          sentiment: feedback.class,
          message: feedback.text || feedback.message || feedback.comment,
        })),
        emptyLabel: 'Sem feedbacks registrados.',
      },
      {
        title: `Reservas (${reservas.length})`,
        columns: [
          { key: 'date', label: 'Data e hora', width: 45 },
          { key: 'people', label: 'Pessoas', width: 25 },
          { key: 'origin', label: 'Origem', width: 52 },
          { key: 'status', label: 'Status', width: 64 },
        ],
        rows: reservas.map((reserva) => ({
          date: [formatDate(reserva.date), toText(reserva.time, '')].filter(Boolean).join(' '),
          people: Number(reserva.people || 0) || '-',
          origin: reserva.origin,
          status: reserva.status,
        })),
        emptyLabel: 'Sem reservas registradas.',
      },
      ...(intelligence.length ? [{
        title: 'Insights registrados',
        paragraphs: intelligence,
      }] : []),
    ],
  };
}

function renderCrmReport(doc, report) {
  let pageNumber = 1;
  let cursorY = 0;

  const drawPageHeader = () => {
    doc.setFillColor(79, 70, 229);
    doc.rect(0, 0, PDF_PAGE_WIDTH, 28, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text(toText(report.title, 'CRM'), PDF_MARGIN, 12);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(toText(report.subtitle, ''), PDF_MARGIN, 19);
    doc.text(`Página ${pageNumber}`, PDF_PAGE_WIDTH - PDF_MARGIN, 19, { align: 'right' });
    doc.setTextColor(15, 23, 42);
    cursorY = 36;
  };

  const addPage = () => {
    doc.addPage();
    pageNumber += 1;
    drawPageHeader();
  };

  const ensureSpace = (height, onNewPage) => {
    if (cursorY + height <= PDF_FOOTER_LIMIT) return;
    addPage();
    onNewPage?.();
  };

  const drawSectionTitle = (title) => {
    ensureSpace(12);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(79, 70, 229);
    doc.text(toText(title), PDF_MARGIN, cursorY);
    doc.setDrawColor(226, 232, 240);
    doc.line(PDF_MARGIN, cursorY + 2, PDF_PAGE_WIDTH - PDF_MARGIN, cursorY + 2);
    doc.setTextColor(15, 23, 42);
    cursorY += 7;
  };

  const drawMetadata = () => {
    const rows = toList(report.metadata);
    rows.forEach(([label, value], index) => {
      const column = index % 2;
      const row = Math.floor(index / 2);
      const x = PDF_MARGIN + (column * (PDF_CONTENT_WIDTH / 2));
      const y = cursorY + (row * 7);
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(100, 116, 139);
      doc.text(`${toText(label)}:`, x, y);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(30, 41, 59);
      doc.text(toText(value), x + 25, y, { maxWidth: (PDF_CONTENT_WIDTH / 2) - 27 });
    });
    cursorY += Math.ceil(rows.length / 2) * 7 + 3;
  };

  const drawHighlights = () => {
    const items = toList(report.highlights);
    if (!items.length) return;
    const gap = 3;
    const width = (PDF_CONTENT_WIDTH - (gap * 3)) / 4;
    ensureSpace(23);
    items.slice(0, 4).forEach(([label, value], index) => {
      const x = PDF_MARGIN + (index * (width + gap));
      doc.setFillColor(248, 250, 252);
      doc.setDrawColor(226, 232, 240);
      doc.roundedRect(x, cursorY, width, 18, 2, 2, 'FD');
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.5);
      doc.setTextColor(100, 116, 139);
      doc.text(toText(label), x + 3, cursorY + 5, { maxWidth: width - 6 });
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(15, 23, 42);
      doc.text(toText(value), x + 3, cursorY + 13, { maxWidth: width - 6 });
    });
    cursorY += 24;
  };

  const drawKeyValues = () => {
    const items = toList(report.keyValues).filter(([, value]) => toText(value, '') !== '');
    if (!items.length) return;
    drawSectionTitle('Dados principais');
    items.forEach(([label, value]) => {
      const lines = doc.splitTextToSize(toText(value), PDF_CONTENT_WIDTH - 48);
      const height = Math.max(7, lines.length * 4 + 2);
      ensureSpace(height, () => drawSectionTitle('Dados principais (continuação)'));
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(100, 116, 139);
      doc.text(toText(label), PDF_MARGIN, cursorY + 3);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(30, 41, 59);
      doc.text(lines, PDF_MARGIN + 45, cursorY + 3);
      cursorY += height;
    });
    cursorY += 2;
  };

  const drawTable = (section) => {
    const columns = toList(section.columns);
    const rows = toList(section.rows);
    drawSectionTitle(section.title);

    const drawTableHeader = () => {
      doc.setFillColor(241, 245, 249);
      doc.rect(PDF_MARGIN, cursorY, PDF_CONTENT_WIDTH, 7, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.5);
      doc.setTextColor(71, 85, 105);
      let x = PDF_MARGIN;
      columns.forEach((column) => {
        doc.text(toText(column.label), x + 2, cursorY + 4.5, { maxWidth: column.width - 4 });
        x += column.width;
      });
      cursorY += 7;
    };

    if (!rows.length) {
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(7.5);
      doc.setTextColor(100, 116, 139);
      doc.text(toText(section.emptyLabel, 'Sem registros.'), PDF_MARGIN, cursorY + 4);
      cursorY += 9;
      return;
    }

    drawTableHeader();
    rows.forEach((row, rowIndex) => {
      const cellLines = columns.map((column) => (
        doc.splitTextToSize(toText(row[column.key]), Math.max(8, column.width - 4))
      ));
      const maxLines = Math.max(1, ...cellLines.map((lines) => lines.length));
      const rowHeight = Math.max(7, (maxLines * 3.3) + 3);
      ensureSpace(rowHeight, drawTableHeader);
      if (rowIndex % 2 === 1) {
        doc.setFillColor(248, 250, 252);
        doc.rect(PDF_MARGIN, cursorY, PDF_CONTENT_WIDTH, rowHeight, 'F');
      }
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.5);
      doc.setTextColor(30, 41, 59);
      let x = PDF_MARGIN;
      columns.forEach((column, columnIndex) => {
        doc.text(cellLines[columnIndex], x + 2, cursorY + 4);
        x += column.width;
      });
      doc.setDrawColor(226, 232, 240);
      doc.line(PDF_MARGIN, cursorY + rowHeight, PDF_PAGE_WIDTH - PDF_MARGIN, cursorY + rowHeight);
      cursorY += rowHeight;
    });
    cursorY += 4;
  };

  const drawParagraphs = (section) => {
    drawSectionTitle(section.title);
    toList(section.paragraphs).forEach((paragraph) => {
      const lines = doc.splitTextToSize(`• ${toText(paragraph)}`, PDF_CONTENT_WIDTH);
      const height = lines.length * 4 + 3;
      ensureSpace(height, () => drawSectionTitle(`${section.title} (continuação)`));
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      doc.setTextColor(30, 41, 59);
      doc.text(lines, PDF_MARGIN, cursorY + 3);
      cursorY += height;
    });
  };

  drawPageHeader();
  drawMetadata();
  drawHighlights();
  drawKeyValues();
  toList(report.sections).forEach((section) => {
    if (section.columns) drawTable(section);
    if (section.paragraphs) drawParagraphs(section);
  });

  const totalPages = doc.getNumberOfPages();
  for (let page = 1; page <= totalPages; page += 1) {
    doc.setPage(page);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.setTextColor(148, 163, 184);
    doc.text(
      `Gerado em ${new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date())}`,
      PDF_MARGIN,
      PDF_PAGE_HEIGHT - 7,
    );
    doc.text('ClientCenter CRM', PDF_PAGE_WIDTH - PDF_MARGIN, PDF_PAGE_HEIGHT - 7, { align: 'right' });
  }
}

export async function downloadCrmPdf(report, options = {}) {
  const JsPdfConstructor = options.JsPdfConstructor || await import('jspdf').then(
    (module) => module.jsPDF || module.default,
  );
  const doc = new JsPdfConstructor({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  doc.setProperties({
    title: toText(report?.title, 'Relatório do CRM'),
    subject: toText(report?.subtitle, 'ClientCenter CRM'),
    creator: 'FoodStation ClientCenter CRM',
  });
  renderCrmReport(doc, report || {});
  doc.save(toText(report?.fileName, 'crm.pdf'));
  return report?.fileName || 'crm.pdf';
}

